import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
    FaRobot,
    FaSyncAlt,
    FaChargingStation,
    FaWalking,
    FaCheckCircle,
    FaBan
} from "react-icons/fa";

import "./RobotFleet.css";

import { getMenuPermissions } from "../utils/auth";
import { StatusPill, BatteryBar } from "../components/RobotStatusUI";

// API_URL is the API base, e.g. http://172.20.10.2:8000/api
const API_URL = process.env.REACT_APP_API_URL;

const FLEET_ENDPOINT = `${API_URL}/portal/robots/fleet`;
const SUMMARY_ENDPOINT = `${API_URL}/portal/robots/fleet/summary`;
const SYNC_ENDPOINT = `${API_URL}/portal/robots/fleet/sync`;

const AUTO_REFRESH_MS = 30000; // refresh fleet table every 30 seconds

function goChargingEndpoint(robotSn) {

    return `${API_URL}/portal/robots/${encodeURIComponent(robotSn)}/commands/go-charging`;

}

function navigateEndpoint(robotSn) {

    return `${API_URL}/portal/robots/${encodeURIComponent(robotSn)}/commands/navigate`;

}

function formatDateTime(value) {

    if (!value) {
        return "-";
    }

    return new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });

}

// Outcome is computed server-side by comparing what the robot's own
// telemetry actually did after this command was sent - see
// robot_service.compute_command_outcome. A "sent" status only means
// Orion's cloud accepted the command; this is what actually tells you
// whether the robot responded.

// ==============================================================
// Main page
// ==============================================================

function RobotFleet() {

    const navigate = useNavigate();

    // Sync-now and the quick-action charge/reception icons are all
    // POST calls, gated by require_menu_permission("robot_fleet").
    const perms = getMenuPermissions("robot_fleet");

    const [fleet, setFleet] = useState([]);

    const [summary, setSummary] = useState(null);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState(null);

    const [syncing, setSyncing] = useState(false);

    const [lastRefreshed, setLastRefreshed] = useState(null);

    const [quickActionBusy, setQuickActionBusy] = useState({}); // { [robot_sn]: "charge" | "nav" }

    const [quickActionNote, setQuickActionNote] = useState(null); // { robot_sn, ok, text }

    const intervalRef = useRef(null);

    const loadFleet = useCallback(async () => {

        setError(null);

        try {

            const [fleetRes, summaryRes] = await Promise.all([
                fetch(FLEET_ENDPOINT),
                fetch(SUMMARY_ENDPOINT)
            ]);

            if (!fleetRes.ok) {
                throw new Error(`Request failed with status ${fleetRes.status}`);
            }

            const fleetData = await fleetRes.json();

            setFleet(fleetData);

            if (summaryRes.ok) {

                setSummary(await summaryRes.json());

            }

            setLastRefreshed(new Date());

        } catch (err) {

            console.error("Fleet load failed:", err);

            setError(err.message || "Unable to reach the robot fleet API");

        } finally {

            setLoading(false);

        }

    }, []);

    const handleSync = useCallback(async () => {

        setSyncing(true);

        try {

            const res = await fetch(SYNC_ENDPOINT, { method: "POST" });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            await loadFleet();

        } catch (err) {

            console.error("Manual sync failed:", err);

        } finally {

            setSyncing(false);

        }

    }, [loadFleet]);

    const handleQuickCommand = useCallback(async (robotSn, type) => {

        setQuickActionBusy((prev) => ({ ...prev, [robotSn]: type }));

        setQuickActionNote(null);

        const endpoint = type === "charge" ? goChargingEndpoint(robotSn) : navigateEndpoint(robotSn);

        try {

            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ requested_by: "admin-ui" })
            });

            if (!res.ok) {

                const body = await res.json().catch(() => ({}));

                throw new Error(body.detail || `Request failed with status ${res.status}`);

            }

            const data = await res.json();

            setQuickActionNote({
                robot_sn: robotSn,
                ok: true,
                text: type === "charge"
                    ? "Heading to charging dock."
                    : `Heading to ${data.target_pos_name}.`
            });

            await loadFleet();

        } catch (err) {

            console.error("Quick command failed:", err);

            setQuickActionNote({ robot_sn: robotSn, ok: false, text: err.message || "Command failed." });

        } finally {

            setQuickActionBusy((prev) => {
                const next = { ...prev };
                delete next[robotSn];
                return next;
            });

        }

    }, [loadFleet]);

    useEffect(() => {

        loadFleet();

        intervalRef.current = setInterval(loadFleet, AUTO_REFRESH_MS);

        return () => clearInterval(intervalRef.current);

    }, [loadFleet]);

    return (

        <div className="robot-fleet">

            <div className="robot-fleet-header">

                <div>
                    <h2><FaRobot /> Robot Fleet</h2>
                    <p className="robot-fleet-subtitle">
                        Live status from the OrionStar Robot OpenAPI, refreshed every {AUTO_REFRESH_MS / 1000}s.
                    </p>
                </div>

                <div className="robot-fleet-actions">

                    {lastRefreshed && (
                        <span className="last-synced">
                            Last refreshed: {formatDateTime(lastRefreshed)}
                        </span>
                    )}

                    {perms.add &&
                    <button className="sync-btn" onClick={handleSync} disabled={syncing}>
                        <FaSyncAlt className={syncing ? "spin" : ""} />
                        {syncing ? "Syncing..." : "Sync now"}
                    </button>
                    }

                </div>

            </div>

            {summary && (

                <div className="fleet-summary-grid">

                    <div className="summary-card">
                        <div className="summary-value">{summary.total_robots}</div>
                        <div className="summary-label">Total robots</div>
                    </div>

                    <div className="summary-card">
                        <div className="summary-value">{summary.online}</div>
                        <div className="summary-label">Online</div>
                    </div>

                    <div className="summary-card">
                        <div className="summary-value">{summary.offline}</div>
                        <div className="summary-label">Offline</div>
                    </div>

                    <div className="summary-card">
                        <div className="summary-value">{summary.idle}</div>
                        <div className="summary-label">Idle</div>
                    </div>

                    <div className="summary-card">
                        <div className="summary-value">{summary.charging}</div>
                        <div className="summary-label">Charging</div>
                    </div>

                    <div className="summary-card summary-alert">
                        <div className="summary-value">{summary.low_battery}</div>
                        <div className="summary-label">Low battery</div>
                    </div>

                </div>

            )}

            <div className="fleet-table-wrapper">

                <table className="fleet-table">

                    <thead>
                        <tr>
                            <th>Robot</th>
                            <th>Status</th>
                            <th>Battery</th>
                            <th>Location</th>
                            <th>Last update</th>
                            <th>Quick actions</th>
                        </tr>
                    </thead>

                    <tbody>

                        {loading && (
                            <tr>
                                <td colSpan={6} className="fleet-table-message">
                                    <FaSyncAlt className="spin" /> Loading fleet...
                                </td>
                            </tr>
                        )}

                        {!loading && error && (
                            <tr>
                                <td colSpan={6} className="fleet-table-message">{error}</td>
                            </tr>
                        )}

                        {!loading && !error && fleet.length === 0 && (
                            <tr>
                                <td colSpan={6} className="fleet-table-message">
                                    No robots registered yet. Check ORION_APPID / ORION_OV_CORPID
                                    on the middleware, or click "Sync now".
                                </td>
                            </tr>
                        )}

                        {!loading && !error && fleet.map((robot) => (

                            <tr key={robot.robot_sn} onClick={() => navigate(`/robot-fleet/${encodeURIComponent(robot.robot_sn)}`)}>

                                <td>
                                    <div className="robot-name-cell">
                                        <strong>{robot.robot_name || robot.robot_sn}</strong>
                                        <span>{robot.deployment_zone || robot.robot_sn}</span>
                                    </div>
                                </td>

                                <td><StatusPill robot={robot} /></td>

                                <td><BatteryBar pct={robot.battery_rate} /></td>

                                <td>{robot.pos_name || "-"}</td>

                                <td>{formatDateTime(robot.last_polled_at)}</td>

                                <td onClick={(e) => e.stopPropagation()}>

                                    <div className="quick-actions-cell">

                                        {perms.add &&
                                        <button
                                            className="quick-btn quick-charge"
                                            title="Send to charging"
                                            disabled={!!quickActionBusy[robot.robot_sn]}
                                            onClick={() => handleQuickCommand(robot.robot_sn, "charge")}
                                        >
                                            <FaChargingStation className={quickActionBusy[robot.robot_sn] === "charge" ? "spin" : ""} />
                                        </button>
                                        }

                                        {perms.add &&
                                        <button
                                            className="quick-btn quick-nav"
                                            title="Send to reception"
                                            disabled={!!quickActionBusy[robot.robot_sn]}
                                            onClick={() => handleQuickCommand(robot.robot_sn, "nav")}
                                        >
                                            <FaWalking className={quickActionBusy[robot.robot_sn] === "nav" ? "spin" : ""} />
                                        </button>
                                        }

                                        {quickActionNote && quickActionNote.robot_sn === robot.robot_sn && (

                                            <span className={`quick-note ${quickActionNote.ok ? "ok" : "err"}`}>
                                                {quickActionNote.ok ? <FaCheckCircle /> : <FaBan />}
                                            </span>

                                        )}

                                    </div>

                                </td>

                            </tr>

                        ))}

                    </tbody>

                </table>

            </div>

        </div>

    );

}

export default RobotFleet;
